import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/chart";
import ChartTemplate from "@/components/chart-template";
import { RevenueByAdTypeRow } from "@/http/api-types";
import { getAdTypeLabel, getCombinedLabel } from "@/lib/mappings";
import { largeNumberFormat, numberFormat } from "@/lib/utils";
import { Bar, BarChart, LabelList, XAxis, YAxis } from "recharts";

interface RevenueByAdPlacementTypeBarChartProps {
  data: RevenueByAdTypeRow[];
}

export default function RevenueByAdPlacementTypeBarChart({ data }: RevenueByAdPlacementTypeBarChartProps) {
  if (!data || data.length === 0) {
    return (
      <ChartTemplate
        chart={
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            <div className="text-center">
              <div className="text-lg font-medium mb-2">No Data</div>
              <div className="text-sm">No revenue data available for the selected filters</div>
            </div>
          </div>
        }
        table={null}
        title="Revenue by Ad & Placement Type"
        description="No data available"
        summaryMetric="N/A"
        summaryMetricLabel="Top Combination"
        timeSplittable={false}
      />
    );
  }

  const totalRevenue = data.reduce((sum, item) => sum + item.revenue_eur, 0);
  const topAdType = data.reduce((prev, current) => (prev.revenue_eur > current.revenue_eur ? prev : current));

  const chartData = data
    .map((item, index) => ({
      ad_type: item.ad_type,
      placement_group: item.placement_group,
      ad_type_label: getAdTypeLabel(item.ad_type),
      combined_label: getCombinedLabel(item.ad_type, item.placement_group),
      revenue_eur: item.revenue_eur,
      ap_conversions: item.ap_conversions,
      secondary_ap_conversions: item.secondary_ap_conversions,
      percentage: ((item.revenue_eur / totalRevenue) * 100).toFixed(1),
      fill: `hsl(var(--chart-${(index % 20) + 1}))`,
    }))
    .sort((a, b) => b.revenue_eur - a.revenue_eur);

  const chartConfig = data.reduce((config, item, index) => {
    const key = `${item.ad_type}_${item.placement_group}`;
    config[key] = {
      label: getCombinedLabel(item.ad_type, item.placement_group),
      color: `hsl(var(--chart-${(index % 20) + 1}))`,
    };
    return config;
  }, {} as ChartConfig);

  const CustomTooltipContent = ({ active, payload }: any) => {
    if (!active || !payload?.length) {
      return null;
    }

    const data = payload[0].payload;
    const cpi = data.revenue_eur > 0 && data.ap_conversions > 0 ? data.revenue_eur / data.ap_conversions : 0;
    const cpa =
      data.revenue_eur > 0 && data.secondary_ap_conversions > 0 ? data.revenue_eur / data.secondary_ap_conversions : 0;

    return (
      <div className="grid min-w-[12rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: data.fill }} />
            <span className="font-medium">{data.combined_label}</span>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs">
            <span>{numberFormat(data.revenue_eur)}</span>
            <span className="text-muted-foreground">{data.percentage}%</span>
          </div>
        </div>
        {(cpi > 0 || cpa > 0) && (
          <div className="grid gap-1 text-xs text-muted-foreground">
            {cpi > 0 && (
              <div className="flex justify-between">
                <span>CPI:</span>
                <span className="font-mono">{numberFormat(cpi)}</span>
              </div>
            )}
            {cpa > 0 && (
              <div className="flex justify-between">
                <span>CPA:</span>
                <span className="font-mono">{numberFormat(cpa)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const chart = (
    <div className="w-full h-full">
      <ChartContainer config={chartConfig} className="w-full h-full">
        <BarChart
          data={chartData}
          layout="vertical"
          height={400}
          barCategoryGap={chartData.length < 8 ? `${(8 - chartData.length) * 10}%` : "10%"}
          margin={{
            top: 20,
            right: 20,
            left: 20,
            bottom: 20,
          }}
        >
          <XAxis type="number" tickFormatter={value => largeNumberFormat(value)} className="text-xs" />
          <YAxis
            type="category"
            dataKey="combined_label"
            className="text-xs"
            width={120}
            axisLine={false}
            tickLine={false}
          />
          <ChartTooltip cursor={false} content={<CustomTooltipContent />} />
          <Bar dataKey="revenue_eur" radius={5}>
            <LabelList
              dataKey="percentage"
              content={(props: any) => {
                const { x, y, width, height, value } = props;
                const barWidth = width;
                const textWidth = value.toString().length * 8;
                const isInside = barWidth > textWidth + 20;

                return (
                  <text
                    x={isInside ? x + width - 8 : x + width + 8}
                    y={y + height / 2}
                    textAnchor={isInside ? "end" : "start"}
                    dominantBaseline="middle"
                    className="text-xs font-medium"
                    fill={isInside ? "white" : "currentColor"}
                    style={isInside ? { textShadow: "1px 1px 4px rgba(0,0,0,0.7)" } : {}}
                  >
                    {value}%
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );

  return (
    <ChartTemplate
      chart={chart}
      table={null}
      title="Revenue by Ad & Placement Type"
      description="Revenue breakdown across different ad and placement type combinations"
      summaryMetric={getCombinedLabel(topAdType.ad_type, topAdType.placement_group)}
      summaryMetricLabel="Top Combination"
      timeSplittable={false}
    />
  );
}
